/**
 * screenplayPaginator — splits ordered screenplay elements into fixed-height
 * pages at 55-line boundaries. Pure function, no DOM, no React.
 *
 * Used by ScriptLining to render deterministic pages at a canonical 540px width
 * that CSS-scales to fit any container.
 */

import type { ScreenplayElement } from './screenplayHelpers.ts';

// ─── Constants (exported for CSS layout calculations) ─────────────────

export const CANONICAL_WIDTH = 540;   // px — 60 Courier chars at ~9px
export const LINES_PER_PAGE = 55;     // standard screenplay page
export const LINE_HEIGHT_PX = 20.8;   // 13px × 1.6
export const PAGE_HEIGHT_PX = LINES_PER_PAGE * LINE_HEIGHT_PX; // ~1144px
export const PAGE_PADDING_X = 20;     // px each side

// Character widths at 13px Courier New inside 500px usable area
const ACTION_CHARS_PER_LINE = 64;     // ~7.8px/char
const DIALOGUE_CHARS_PER_LINE = 50;   // 7ch indent each side narrows usable area

// ─── Types ────────────────────────────────────────────────────────────

export interface ScreenplayPage {
  pageNumber: number;           // 1-based
  elements: ScreenplayElement[];
  startElementIndex: number;    // index into flat ordered-elements array
  endElementIndex: number;      // inclusive
}

// ─── Line estimation per element ──────────────────────────────────────

function estimateLines(el: ScreenplayElement): number {
  switch (el.type) {
    case 'action': {
      const text = el.data.actionText || '';
      return Math.ceil(Math.max(text.length, 1) / ACTION_CHARS_PER_LINE) + 1; // +1 margin
    }
    case 'dialogue': {
      let lines = 1; // character name
      if (el.data.parenthetical) lines += 1;
      const text = el.data.dialogueText || '';
      lines += Math.ceil(Math.max(text.length, 1) / DIALOGUE_CHARS_PER_LINE);
      lines += 1; // margin
      return lines;
    }
    case 'transition':
      return 2; // text + margin
    default:
      return 1;
  }
}

// ─── Core pagination ──────────────────────────────────────────────────

/**
 * Paginate screenplay elements into fixed pages.
 * Deterministic given the same elements — no DOM measurement needed.
 *
 * Avoids breaking mid-dialogue-block: if a dialogue element won't fit
 * on the current page, the entire block is pushed to the next page.
 */
export function paginateScreenplay(
  elements: ScreenplayElement[],
  _sceneId: number,
): ScreenplayPage[] {
  if (elements.length === 0) {
    return [{ pageNumber: 1, elements: [], startElementIndex: 0, endElementIndex: -1 }];
  }

  const pages: ScreenplayPage[] = [];
  let currentPageElements: ScreenplayElement[] = [];
  let currentLines = 0;
  let pageStartIdx = 0;

  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    const elLines = estimateLines(el);

    // Check if this element fits on the current page
    if (currentLines + elLines > LINES_PER_PAGE && currentPageElements.length > 0) {
      // Close current page
      pages.push({
        pageNumber: pages.length + 1,
        elements: currentPageElements,
        startElementIndex: pageStartIdx,
        endElementIndex: i - 1,
      });
      currentPageElements = [];
      currentLines = 0;
      pageStartIdx = i;
    }

    currentPageElements.push(el);
    currentLines += elLines;
  }

  // Close last page
  if (currentPageElements.length > 0) {
    pages.push({
      pageNumber: pages.length + 1,
      elements: currentPageElements,
      startElementIndex: pageStartIdx,
      endElementIndex: elements.length - 1,
    });
  }

  return pages;
}
